import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog.jsx'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { TypographySmall, TypographySmallMuted, TypographyXSmallMuted } from '@/components/ui/typography.jsx'
import { FormBlock } from '../forms/FormBlock'
import { UserRoles } from '@/consts/index.js'
import { Icon } from '@iconify/react'

export const MemberDialog = ({ open, onOpenChange, selectedMember, onSubmit, title, description, navigateToAccountMembersSettingsPage, spaceName }) => {

    const [chosenRole, setChosenRole] = useState(selectedMember?.role || '')
    const [email, setEmail] = useState(selectedMember?.email || '')
    const [errors, setErrors] = useState({})

    useEffect(() => {
        if (open) {
            setChosenRole(selectedMember?.role || '')
            setEmail(selectedMember?.email || '')
            setErrors({})
        }
    }, [open, selectedMember])

    const validateForm = () => {
        const newErrors = {}
        
        // Validate email
        if (!email) {
            newErrors.email = 'Email is required'
        } 
        
        if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Please enter a valid email address'
        }  
        
        // Validate role
        if (!chosenRole) {
            newErrors.role = 'Please select a role'
        }
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = () => {
        const isValid = validateForm()
        if (isValid) {
            onSubmit({ 
                user: selectedMember?.user, 
                email: email.trim().toLowerCase(), 
                role: chosenRole 
            })
        }
    }

    const handleEmailChange = (e) => {
        setEmail(e.target.value)
        if (errors.email) {
            setErrors({ ...errors, email: null })
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl overflow-y-scroll">
                <DialogHeader>
                    <DialogTitle>{title || (selectedMember ? 'Edit Member' : 'Add Member')}</DialogTitle>
                    <DialogDescription>
                        {description || (selectedMember ? 'Update member information' : 'Add a new member')}
                    </DialogDescription>
                </DialogHeader>

                <FormBlock 
                    label="Email" 
                    required
                >
                    <Input
                        id="member-email"
                        name="email"
                        type="email"
                        placeholder="member@example.com"
                        value={email}
                        onChange={handleEmailChange}
                        disabled={selectedMember}
                        className={errors.email ? "border-destructive" : ""}
                        error={errors.email}
                    />
                </FormBlock> 
                
                <div className="space-y-8 py-4">
                    <div className="">
                        <TypographySmall>
                            Role
                        </TypographySmall>
                        <div className={`mb-3 mt-2 rounded-md ${errors.role ? "border border-destructive" : ""}`}>
                            <div className="space-y-3">
                                <div 
                                    onClick={() => {
                                        setChosenRole(UserRoles.ADMIN)
                                        if (errors.role) {
                                            setErrors({ ...errors, role: null })
                                        }
                                    }} 
                                    className={`relative p-4 transition-all hover:bg-accent rounded-md cursor-pointer border  ${chosenRole === UserRoles.ADMIN ? 'bg-accent border-primary' : ''}`}
                                >
                                    <TypographySmall className="font-medium">Admin</TypographySmall>
                                    <TypographySmallMuted className="block mt-1">Full access to manage resources, invite members, and modify settings.</TypographySmallMuted>
                                </div>
                                <div 
                                    onClick={() => {
                                        setChosenRole(UserRoles.EDITOR)
                                        if (errors.role) setErrors({ ...errors, role: null })
                                    }} 
                                    className={`relative p-4 transition-all hover:bg-accent rounded-md cursor-pointer border  ${chosenRole === UserRoles.EDITOR ? 'bg-accent border-primary' : ''}`}
                                >
                                    <TypographySmall className="font-medium">Editor</TypographySmall>
                                    <TypographySmallMuted className="block mt-1">Can edit content but cannot invite or manage members.</TypographySmallMuted>
                                </div>
                                <div 
                                    onClick={() => {
                                        setChosenRole(UserRoles.VIEWER)
                                        if (errors.role) setErrors({ ...errors, role: null })
                                    }} 
                                    className={`relative p-4 transition-all hover:bg-accent rounded-md cursor-pointer border  ${chosenRole === UserRoles.VIEWER ? 'bg-accent border-primary' : ''}`}
                                >
                                    <TypographySmall className="font-medium">Viewer</TypographySmall>
                                    <TypographySmallMuted className="block mt-1">Read-only access. Cannot edit content or invite members.</TypographySmallMuted>
                                </div>
                            </div>
                        </div>
                        {errors.role && (
                            <p className="text-sm text-destructive mt-1">{errors.role}</p>
                        )}
                    </div>

                    {
                        navigateToAccountMembersSettingsPage && (
                            <div className="text-center border p-2 bg-secondary/20 rounded">
                                <Icon icon="material-symbols:info-outline-rounded" className="m-auto my-1 text-secondary w-5 h-5" />
                                <TypographySmallMuted>
                                    You're currently inviting member to join the space - <span className="font-bold">{spaceName}</span>
                                </TypographySmallMuted>
                                <TypographySmallMuted>
                                    If you want to invite a member to have an account access level
                                </TypographySmallMuted>
                                <Button
                                    onClick={navigateToAccountMembersSettingsPage}
                                    variant="ghost"
                                    className="underline"
                                    size="mini"
                                >
                                    Click here
                                </Button>
                            </div>
                        )
                    }


                </div>
                
                <div className="flex justify-end gap-2">
                    <Button 
                        variant="outline" 
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit}>
                        {selectedMember ? 'Update' : 'Add'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
